/**
 * AdminOcrPage — 后台 OCR 识别记录
 *
 * 功能：
 *  - 显示当前 OCR 队列状态（运行中 / 排队中 / 最大并发数）
 *  - 列出近 7 天所有代传批次的识别情况（管理员看全体，代理看名下）
 *  - 识别中：显示进度条；识别完成：显示成功/重复/无效/失败 badge
 *  - 点击批次【原地展开】详情（成功配对/无效/失败列表），不跳转页面——
 *    跟前台 HistoryPage 的交互一致，复用同一个 OcrGroupDetail 组件
 *  - 展开后可直接手动填写号码 / 重新识别，不用跳去图片管理页
 *  - 识别完成后通过 Socket ocr_batch_done 实时刷新 + 顶部气泡通知
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';
import BizSwitch from '../../components/BizSwitch';
import SubTabs from '../../components/SubTabs';
import { setBizMode } from '../../utils/bizMode';
import Modal, { useConfirm } from '../../components/Modal';
import OcrGroupDetail from '../../components/OcrGroupDetail';
import api from '../../utils/api';
import { useSocket } from '../../contexts/SocketContext';
import { useToast } from '../../components/Toast';
import { useBeijingToday } from '../../utils/useBeijingToday';
import {
  ArrowLeft, Loader, X, ScanLine, CheckCircle2,
  ChevronDown, AlertTriangle, Clock, ExternalLink,
  Check, RotateCcw, AlertCircle, XCircle, Trash2
} from 'lucide-react';

function OcrBadges({ ocr, small }) {
  const sz = small ? 10 : 11;
  const icSz = small ? 9 : 10;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {ocr.qualified > 0 && (
        <span style={{ fontSize: sz, background: 'rgba(52,199,89,.12)', color: 'var(--c-success)', borderRadius: 6, padding: '1px 6px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <Check size={icSz} strokeWidth={2.5} /> {ocr.qualified} 组
        </span>
      )}
      {ocr.duplicate > 0 && (
        <span style={{ fontSize: sz, background: 'rgba(255,149,0,.12)', color: 'var(--c-accent)', borderRadius: 6, padding: '1px 6px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <RotateCcw size={icSz} strokeWidth={2.5} /> {ocr.duplicate}
        </span>
      )}
      {ocr.invalid > 0 && (
        <span style={{ fontSize: sz, background: 'rgba(255,59,48,.1)', color: 'var(--c-danger)', borderRadius: 6, padding: '1px 6px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <AlertCircle size={icSz} strokeWidth={2.5} /> {ocr.invalid}
        </span>
      )}
      {ocr.failed > 0 && (
        <span style={{ fontSize: sz, background: 'var(--c-bg)', color: 'var(--c-text-secondary)', borderRadius: 6, padding: '1px 6px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <XCircle size={icSz} strokeWidth={2.5} /> {ocr.failed}
        </span>
      )}
    </div>
  );
}

export default function AdminOcrPage() {
  const navigate = useNavigate();
  const { subscribe } = useSocket();
  const { showToast } = useToast();

  const confirm = useConfirm();
  const today = useBeijingToday();   // “今日批次”统计用：groups 本身是近 7 天全量，不能直接拿 groups.length 当“今日”
  const [groups, setGroups]       = useState([]);
  const [queueStatus, setQueue]   = useState({ active: 0, waiting: 0, max: 2 });
  const [loading, setLoading]     = useState(true);
  const [doneBanner, setDoneBanner] = useState(null); // { stats, userName, groupUUID }
  const bannerTimer = useRef(null);

  // 展开状态：哪个批次展开显示详情
  const [expanded, setExpanded] = useState({}); // group.id -> bool
  const [deletingGroupId, setDeletingGroupId] = useState(null); // 正在删除的批次 id

  // 手动填写弹窗状态（跟 WorkspacePage/HistoryPage 保持一致的交互）
  const [manualTarget, setManualTarget] = useState(null); // { imageId }
  const [manualPhone, setManualPhone]   = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualCheck, setManualCheck]   = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/images/admin-ocr-groups');
      setGroups(res.data.groups || []);
      setQueue(res.data.queueStatus || { active: 0, waiting: 0, max: 2 });
    } catch (e) {
      // 静默失败
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 实时刷新
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (event === 'ocr_progress') {
        // 直接用 payload 更新对应批次的进度，不等 refetch
        // 避免「管理员收不到推送」或「中间状态不存库」导致进度条永远 0%
        if (payload && payload.groupUUID != null) {
          setGroups(prev => prev.map(g => {
            if (g.groupUUID !== payload.groupUUID) return g;
            return {
              ...g,
              ocr: {
                ...g.ocr,
                _progress_done : payload.done  != null ? payload.done  : g.ocr._progress_done,
                _progress_total: payload.total != null ? payload.total : g.ocr._progress_total,
                running: true,
                done: false,
              },
            };
          }));
        }
        // 仍保留延迟 refetch，用于同步 queue 状态等其他字段
        clearTimeout(window._adminOcrProgressTimer);
        window._adminOcrProgressTimer = setTimeout(() => fetchData(), 1500);
      } else if (['image_stats_refresh'].includes(event)) {
        setTimeout(() => fetchData(), 400);
      } else if (event === 'stats_refresh') {
        clearTimeout(window._adminOcrStatsTimer);
        window._adminOcrStatsTimer = setTimeout(() => fetchData(), 1200);
      }
      if (event === 'ocr_batch_done') {
        fetchData();
        const s = payload?.stats || {};
        const parts = [];
        if (s.qualified > 0) parts.push(`成功 ${s.qualified} 组`);
        if (s.duplicate > 0) parts.push(`重复 ${s.duplicate} 张`);
        if ((s.invalid || 0) + (s.failed || 0) > 0) parts.push(`待处理 ${(s.invalid||0)+(s.failed||0)} 张`);
        if (parts.length > 0) showToast(`OCR 识别完成：${parts.join('，')}`, 'success');

        // 顶部气泡通知
        const group = groups.find(g => g.groupUUID === payload?.groupUUID);
        if (bannerTimer.current) clearTimeout(bannerTimer.current);
        setDoneBanner({ stats: s, userName: group?.userName || '成员', groupUUID: payload?.groupUUID });
        bannerTimer.current = setTimeout(() => setDoneBanner(null), 10000);
      }
    });
  }, [subscribe, fetchData, showToast, groups]);

  // 按日期分组
  const byDate = groups.reduce((acc, g) => {
    const d = g.uploadDate || '';
    if (!acc[d]) acc[d] = [];
    acc[d].push(g);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', color: 'var(--c-primary)', display: 'flex', alignItems: 'center' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>OCR 识别记录</span>
        {/* 数据靠 Socket 实时刷新（ocr_progress / ocr_batch_done / image_stats_refresh / stats_refresh），不需要手动刷新按钮 */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
        </div>
      </div>

      {/* 排五 / 证券 切换：证券的“记录”是证券记录页，底部“记录”标签也随业务切换 */}
      <div style={{ padding: '10px 16px 0' }}>
        <BizSwitch value="legacy" onChange={(k) => { if (k === 'puzzle') { setBizMode('puzzle'); navigate('/admin/puzzle-records', { replace: true }); } }} />
      </div>
      {/* 识别记录 | 图片管理：一键互相切换（和证券记录页的两个标签对应） */}
      <div style={{ padding: '8px 16px 0' }}>
        <SubTabs items={[['records', '识别记录'], ['images', '图片管理']]} value="records"
          onChange={(k) => { if (k === 'images') navigate('/admin/images', { replace: true }); }} />
      </div>

      {/* OCR 完成气泡 */}
      {doneBanner && (() => {
        const s = doneBanner.stats || {};
        const hasBad = (s.invalid || 0) + (s.failed || 0) > 0;
        const parts = [];
        if (s.qualified > 0) parts.push(`成功 ${s.qualified} 组`);
        if (s.duplicate > 0) parts.push(`重复 ${s.duplicate} 张`);
        if (hasBad) parts.push(`待处理 ${(s.invalid||0)+(s.failed||0)} 张`);
        return (
          <div style={{
            margin: '8px 16px 0',
            background: hasBad ? 'rgba(255,149,0,0.08)' : 'rgba(52,199,89,0.08)',
            border: `1px solid ${hasBad ? 'rgba(255,149,0,0.3)' : 'rgba(52,199,89,0.3)'}`,
            borderRadius: 12, padding: '11px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'slideUpBanner .2s ease',
          }}>
            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {hasBad
                ? <AlertTriangle size={18} color="var(--c-accent)" />
                : <CheckCircle2 size={18} color="var(--c-success)" />}
            </span>
            <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
              <strong>{doneBanner.userName}</strong> 识别完成：{parts.join('，')}
            </div>
            <button onClick={() => setDoneBanner(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-tertiary)', padding: 2 }}>
              <X size={14} />
            </button>
          </div>
        );
      })()}

      <div style={{ padding: '12px 16px' }}>

        {/* 队列状态卡 */}
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text-secondary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={14} /> OCR 队列状态
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--c-border)', borderRadius: 10, overflow: 'hidden' }}>
            {[
              { label: '识别中', value: queueStatus.active, color: 'var(--c-primary)' },
              { label: '排队中', value: queueStatus.waiting, color: 'var(--c-accent)' },
              { label: '并发上限', value: queueStatus.max, color: 'var(--c-text-secondary)' },
              { label: '今日批次', value: groups.filter(g => g.uploadDate === today).length, color: 'var(--c-text)' },
            ].map(item => (
              <div key={item.label} style={{ background: 'var(--c-surface)', padding: '10px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: item.color }}>{item.value}</div>
                <div style={{ fontSize: 10, color: 'var(--c-text-tertiary)', marginTop: 2 }}>{item.label}</div>
              </div>
            ))}
          </div>
          {queueStatus.waiting > 0 && (
            <div style={{
              marginTop: 10, padding: '8px 12px',
              background: 'rgba(255,149,0,0.07)', border: '1px solid rgba(255,149,0,0.25)',
              borderRadius: 8, fontSize: 12, color: 'var(--c-accent)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Loader size={12} className="spin" />
              {queueStatus.waiting} 个批次等待识别线路，当前 {queueStatus.active}/{queueStatus.max} 线路占用
            </div>
          )}
          {queueStatus.active === 0 && queueStatus.waiting === 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--c-text-tertiary)', textAlign: 'center' }}>
              当前队列空闲
            </div>
          )}
        </div>

        {/* 批次列表 */}
        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中…</p>
        ) : groups.length === 0 ? (
          <div className="card empty-state">
            <ScanLine size={40} color="var(--c-text-secondary)" strokeWidth={1.2} style={{ margin: '0 auto 12px', display: 'block' }} />
            <p>近 7 天无上传记录</p>
          </div>
        ) : dates.map(date => (
          <div key={date}>
            <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', fontWeight: 600, margin: '14px 2px 6px' }}>
              {date}
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {byDate[date].map((group, i) => {
                const ocr = group.ocr;
                const isRunning = ocr.running;
                const isQueued  = ocr.queued && !isRunning;
                const isDone    = ocr.done;
                const isPending = !isDone && !isRunning && !isQueued && ocr.total === 0;
                const hasBad = (ocr.invalid || 0) + (ocr.failed || 0) > 0;
                // 优先用 socket 实时推送的进度（_progress_*），
                // 识别完成后回退到用结果字段反推（qualified*2 + duplicate + ...）
                const doneCount = ocr.running && ocr._progress_done != null
                  ? ocr._progress_done
                  : (ocr.qualified || 0) * 2 + (ocr.duplicate || 0) + (ocr.invalid || 0) + (ocr.failed || 0);
                const totalCount = ocr.running && ocr._progress_total != null
                  ? ocr._progress_total
                  : ocr.total;
                const pct = totalCount > 0 ? Math.round(doneCount / totalCount * 100) : 0;
                const isOpen = !!expanded[group.id];

                return (
                  <div
                    key={group.id}
                    style={{
                      borderBottom: i < byDate[date].length - 1 ? '0.5px solid var(--c-border)' : 'none',
                    }}
                  >
                    <div
                      onClick={() => isDone && setExpanded(p => ({ ...p, [group.id]: !p[group.id] }))}
                      style={{ padding: '11px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: isDone ? 'pointer' : 'default' }}
                    >
                      {/* 状态图标 */}
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: isRunning ? 'rgba(10,132,255,0.1)' : isDone && !hasBad ? 'rgba(52,199,89,0.1)' : isDone && hasBad ? 'rgba(255,149,0,0.1)' : 'var(--c-bg)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isRunning
                          ? <Loader size={16} color="var(--c-primary)" className="spin" />
                          : isQueued
                          ? <Clock size={16} color="var(--c-accent)" />
                          : isDone && !hasBad
                          ? <CheckCircle2 size={16} color="var(--c-success)" />
                          : isDone && hasBad
                          ? <AlertTriangle size={16} color="var(--c-accent)" />
                          : <ScanLine size={16} color="var(--c-text-tertiary)" />
                        }
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* 用户名 + 批次 */}
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.userName}</span>
                          <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)', fontWeight: 400, flexShrink: 0 }}>
                            第 {group.seq} 批 · {group.image_count} 张
                          </span>
                        </div>

                        {/* 状态 */}
                        {isPending && (
                          <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>等待触发识别…</span>
                        )}
                        {isQueued && (
                          <div style={{ fontSize: 11, color: 'var(--c-accent)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={10} /> 队列中，等待识别线路空闲…
                          </div>
                        )}
                        {isRunning && (
                          <>
                            <div style={{ height: 3, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden', margin: '4px 0 3px' }}>
                              <div style={{ height: '100%', background: 'var(--c-primary)', borderRadius: 2, width: `${pct}%`, transition: 'width .5s' }} />
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>
                              已识别 {doneCount} / {totalCount} 张 · {pct}%
                            </div>
                          </>
                        )}
                        {isDone && <OcrBadges ocr={ocr} small />}
                      </div>

                      {/* 展开箭头 + 删除按钮 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {!group.is_exported && !isRunning && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const ok = await confirm(
                                `确认删除「${group.userName}」第 ${group.seq} 批全部 ${group.image_count} 张图片？`,
                                { detail: '已导出图片不可删除。其余图片将被永久删除，不可恢复。', danger: true }
                              );
                              if (!ok) return;
                              setDeletingGroupId(group.id);
                              try {
                                await api.delete(`/images/upload-group/${group.id}`);
                                showToast('已删除整批图片', 'success');
                                setTimeout(fetchData, 300);
                              } catch (err) {
                                showToast(err.response?.data?.error || '删除失败', 'error');
                              } finally {
                                setDeletingGroupId(null);
                              }
                            }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: 4, color: 'var(--c-danger)', opacity: deletingGroupId === group.id ? 0.5 : 0.6,
                              display: 'flex', alignItems: 'center',
                            }}
                            title="删除整批"
                            disabled={deletingGroupId === group.id}
                          >
                            {deletingGroupId === group.id
                              ? <Loader size={13} className="spin" />
                              : <Trash2 size={13} />
                            }
                          </button>
                        )}
                        {isDone && (
                          <ChevronDown size={14} color="var(--c-text-tertiary)"
                            style={{ marginTop: 4, transform: isOpen ? 'rotate(180deg)' : 'none', transition: '.18s' }} />
                        )}
                      </div>
                    </div>

                    {/* 原地展开详情：复用前台同款组件，不跳转页面 */}
                    {isOpen && isDone && (
                      <div>
                        <OcrGroupDetail
                          groupUUID={group.groupUUID}
                          imagesDeleted={!!group.is_exported}
                          onManualFill={(imageId) => { setManualTarget({ imageId }); setManualPhone(''); setManualCheck(null); }}
                          onReocr={async (imageId) => {
                            try {
                              const r = await api.post('/images/reocr-single', { imageId: String(imageId) });
                              showToast(r?.data?.message || '已加入重新识别队列', 'success');
                              setTimeout(fetchData, 1500);
                            } catch (err) { showToast(err.response?.data?.error || '操作失败', 'error'); }
                          }}
                        />
                        {/* 需要看该用户全部历史图片时，仍保留跳转入口，但不是默认点击行为 */}
                        <div style={{ padding: '8px 14px 12px', borderTop: '1px solid var(--c-divider)' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/images/${group.userId}`); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              fontSize: 12, color: 'var(--c-primary)',
                              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            }}
                          >
                            <ExternalLink size={12} /> 查看「{group.userName}」全部图片记录
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* OCR 手动填写手机号弹窗 */}
      {manualTarget && (
        <Modal show={true} onClose={() => setManualTarget(null)} title="手动填写手机号">
          <div style={{ padding: '4px 0 8px' }}>
            <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 12 }}>图片未能识别到号码，请手动输入：</p>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type="tel"
                placeholder="输入11位手机号"
                maxLength={11}
                value={manualPhone}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                  setManualPhone(v);
                  setManualCheck(null);
                  if (v.length === 11 && /^1[3-9]\d{9}$/.test(v)) {
                    api.post('/images/check-phone', { phone: v })
                      .then(r => setManualCheck(r.data))
                      .catch(() => {});
                  }
                }}
                style={{ letterSpacing: '.06em', paddingRight: 44 }}
                autoFocus
              />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 600, color: manualPhone.length === 11 ? 'var(--c-success)' : 'var(--c-text-tertiary)', pointerEvents: 'none' }}>
                {manualPhone.length}/11
              </span>
            </div>
            {manualPhone.length > 0 && manualPhone.length < 11 && (
              <p style={{ fontSize: 11.5, color: 'var(--c-danger)', marginTop: 5 }}>还需再输入 {11 - manualPhone.length} 位</p>
            )}
            {manualCheck && !manualCheck.valid && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(255,59,48,.08)', color: 'var(--c-danger)' }}>
                {manualCheck.message}
              </div>
            )}
            {manualCheck && manualCheck.valid && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(52,199,89,.08)', color: 'var(--c-success)' }}>
                号码在派单库中，可以提交
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-outline" style={{ flex: 1, height: 44 }}
                onClick={() => { setManualTarget(null); setManualPhone(''); setManualCheck(null); }}>取消</button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, height: 44 }}
                disabled={!/^1[3-9]\d{9}$/.test(manualPhone) || manualLoading || (manualCheck && !manualCheck.valid)}
                onClick={async () => {
                  setManualLoading(true);
                  try {
                    const res = await api.post(`/images/ocr-edit/${manualTarget.imageId}`, { phone: manualPhone });
                    const verify = res.data?.ocrVerify;
                    if (verify?.phone && verify.phone !== manualPhone) {
                      showToast(`⚠ OCR 识别到 ${verify.phone}，与填写不一致，已按手动填写保存`, 'error');
                    } else {
                      showToast('已保存手机号', 'success');
                    }
                    setManualTarget(null);
                    setManualCheck(null);
                    setTimeout(fetchData, 800);
                  } catch (err) { showToast(err.response?.data?.error || '保存失败', 'error'); }
                  finally { setManualLoading(false); }
                }}
              >
                {manualLoading ? '保存中…' : '确认填写'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <TabBar mode="admin" />
    </div>
  );
}
